import { ChangeDetectionStrategy, Component, HostListener, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { DxcIconComponent } from '../icon/dxc-icon.component';
import { DxcSceneComponent } from '../scene/dxc-scene.component';
import { DXC_SCENE_FOR } from '../dxc.types';
import { DxcDialogService, DxcPending } from './dxc-dialog.service';

/**
 * Anfitrión de los diálogos bloqueantes. Se monta una vez, en el layout raíz,
 * junto al `toaster`.
 *
 * Esc y clic en el velo cierran sin confirmar y sin guardar, y el foco vuelve al
 * elemento que abrió el diálogo — eso último lo aporta el navegador al retirar
 * el nodo, siempre que el disparador siga en el DOM.
 */
@Component({
    standalone: true,
    selector: 'dxc-dialog-host',
    imports: [CommonModule, FormsModule, DxcIconComponent, DxcSceneComponent],
    template: `
        @if (dialog.$pending | async; as pending) {
            <div class="dxc-scrim dxc-scrim--fixed"
                [class.is-open]="!(dialog.$closing | async)"
                [class.is-closing]="dialog.$closing | async"
                (click)="onScrim($event)">
                <div class="dxc-dialog"
                    [class.dxc-dialog--outcome]="pending.mode !== 'prompt'"
                    [class.is-open]="!(dialog.$closing | async)"
                    [class.is-closing]="dialog.$closing | async"
                    role="dialog" aria-modal="true" aria-labelledby="dxc-dialog-title">

                    <!-- El lienzo. Un diálogo de decisión también lo lleva: la
                         escena «decision» pone a alguien decidiendo —el equipo en el
                         portón, el conductor asomado— en vez del signo de
                         interrogación genérico que ponía SweetAlert. -->
                    @if (pending.mode !== 'prompt') {
                        <div class="dxc-outcome__stage" [class]="stageClass(pending)">
                            <dxc-scene [name]="sceneOf(pending)" [tone]="toneOf(pending)"></dxc-scene>
                            <button type="button" class="dxc-dialog__close"
                                aria-label="Cerrar" (click)="dialog.dismiss()">
                                <dxc-icon name="close" [size]="18"></dxc-icon>
                            </button>
                        </div>
                    }

                    <div class="dxc-outcome__pane">
                    <div class="dxc-dialog__head">
                        <h4 id="dxc-dialog-title">{{ pending.title }}</h4>
                        @if (pending.description) { <p>{{ pending.description }}</p> }

                        @if (pending.mode === 'outcome') {
                            @if (pending.code) {
                                <span class="dxc-outcome__code">
                                    <dxc-icon name="info" [size]="14"></dxc-icon>{{ pending.code }}
                                </span>
                            }
                            @if (pending.detail) {
                                <!-- «Para soporte», no «detalle técnico»: el
                                     rótulo dice para qué sirve abrirlo. Va
                                     plegado y nunca es lo que el usuario lee
                                     primero — arriba está la traducción. -->
                                <!-- Abierto de serie cuando cabe. Plegar algo
                                     que explica el fallo apuesta a que el
                                     usuario lo despliegue, y no lo hace. Solo
                                     se pliega el volcado largo, porque abierto
                                     empujaría el pie —y con él la salida—
                                     fuera de la pantalla (R10). -->
                                <details class="dxc-outcome__detail" [open]="cabeAbierto(pending.detail)">
                                    <summary>
                                        <dxc-icon name="chevron-down" [size]="14"></dxc-icon>Lo que devolvió el sistema
                                    </summary>
                                    <pre>{{ pending.detail }}</pre>
                                </details>
                            }
                        }
                    </div>

                    @if (pending.mode === 'prompt') {
                        <div class="dxc-dialog__body">
                            <label class="dxc-field">
                                <span class="dxc-field__label">{{ pending.label }}</span>
                                <span class="dxc-field__wrap">
                                    <input class="dxc-input" [class.is-invalid]="!!error()"
                                        [placeholder]="pending.placeholder || ''"
                                        [ngModel]="draft()" (ngModelChange)="onDraft($event)"
                                        (keydown.enter)="submitPrompt(pending)"
                                        name="dxc-prompt" autocomplete="off" autofocus>
                                </span>
                                @if (error()) { <span class="dxc-field__error">{{ error() }}</span> }
                            </label>
                        </div>
                    }

                    <div class="dxc-dialog__foot">
                        @switch (pending.mode) {
                            @case ('outcome') {
                                @if (pending.support && (pending.detail || pending.code)) {
                                    <button type="button" class="dxc-btn dxc-btn--secondary dxc-btn--copy"
                                        (click)="copyForSupport(pending)">
                                        <dxc-icon name="copy" [size]="15"></dxc-icon>{{ copied() ? 'Copiado' : 'Copiar para soporte' }}
                                    </button>
                                }
                                <button type="button" class="dxc-btn dxc-btn--primary" (click)="dialog.answer(true)">
                                    {{ pending.closeLabel }}
                                </button>
                            }
                            @case ('prompt') {
                                <button type="button" class="dxc-btn dxc-btn--neutral" (click)="dialog.answerPrompt(null)">
                                    {{ pending.cancelLabel }}
                                </button>
                                <button type="button" class="dxc-btn dxc-btn--primary" (click)="submitPrompt(pending)">
                                    {{ pending.confirmLabel }}
                                </button>
                            }
                            @case ('choice') {
                                <button type="button" class="dxc-btn dxc-btn--neutral" (click)="dialog.answerChoice(null)">
                                    {{ pending.cancelLabel }}
                                </button>
                                @for (option of pending.options; track option.value) {
                                    <button type="button" class="dxc-btn"
                                        [class.dxc-btn--primary]="!!option.primary"
                                        [class.dxc-btn--secondary]="!option.primary"
                                        (click)="dialog.answerChoice(option.value)">
                                        {{ option.label }}
                                    </button>
                                }
                            }
                            @default {
                                <button type="button" class="dxc-btn dxc-btn--neutral" (click)="dialog.answer(false)">
                                    {{ pending.cancelLabel }}
                                </button>
                                <button type="button" class="dxc-btn"
                                    [class.dxc-btn--primary]="pending.variant !== 'danger'"
                                    [class.dxc-btn--danger]="pending.variant === 'danger'"
                                    (click)="dialog.answer(true)">
                                    {{ pending.confirmLabel }}
                                </button>
                            }
                        }
                    </div>
                    </div>
                </div>
            </div>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DxcDialogHostComponent {

    /** Valor en curso del `prompt`. */
    readonly draft = signal('');
    /**
     * El error del `prompt`. Nace vacío y solo aparece cuando el usuario lo
     * intenta: R4 — el asterisco dice que hace falta, el rojo dice que hizo algo
     * mal. Nada sale en rojo al abrir.
     */
    readonly error = signal('');

    /** Confirma el copiado dentro del propio botón: un toast encima de un diálogo modal se pierde. */
    readonly copied = signal(false);

    #promptFor: DxcPending | null = null;

    constructor(public dialog: DxcDialogService) {
        this.dialog.$pending.subscribe(pending => {
            if (pending?.mode === 'prompt' && pending !== this.#promptFor) {
                this.#promptFor = pending;
                this.draft.set(pending.value ?? '');
                this.error.set('');
            }
            if (!pending) this.#promptFor = null;
            this.copied.set(false);
        });
    }

    sceneOf(pending: DxcPending) {
        if (pending.mode === 'outcome') return pending.scene ?? DXC_SCENE_FOR[pending.kind];
        if (pending.mode === 'confirm' || pending.mode === 'choice') return pending.scene ?? DXC_SCENE_FOR['question'];
        return DXC_SCENE_FOR['question'];
    }

    /**
     * Lo que se pega en un ticket. Lleva el título para que se entienda sin
     * contexto, el código para buscarlo, y el volcado — en ese orden, que es el
     * que hace falta al leerlo del otro lado.
     */
    copyForSupport(pending: DxcPending) {
        if (pending.mode !== 'outcome') return;
        const text = [pending.title, pending.description, pending.code, pending.detail]
            .filter(Boolean).join('\n');
        navigator.clipboard?.writeText(text).then(() => {
            this.copied.set(true);
            setTimeout(() => this.copied.set(false), 2400);
        }).catch(() => { });
    }

    toneOf(pending: DxcPending) {
        if (pending.mode === 'outcome') return pending.tone;
        // Una destructiva se anuncia como tal desde el lienzo: la escena sigue
        // siendo la pregunta —dos rutas— pero teñida de lo que está en juego.
        return pending.mode === 'confirm' && pending.variant === 'danger' ? 'danger' as const : undefined;
    }

    stageClass(pending: DxcPending) {
        return `dxc-outcome__stage--${this.toneOf(pending) ?? 'neutral'}`;
    }

    /**
     * Umbral por encima del cual el volcado se pliega. 240 caracteres son unas
     * tres líneas a este ancho: cabe sin empujar el pie. Un `stack` o un JSON
     * de respuesta pasan de largo y se quedan plegados, que es para lo que el
     * pliegue existe.
     */
    cabeAbierto(detail?: string) { return !!detail && detail.length <= 240; }

    /** Una vez tocada la salida, los botones dejan de responder: si no, dos
     *  pulsaciones rápidas resuelven la promesa dos veces. */
    get leaving() { return this.dialog.$closing.value; }

    onDraft(value: string) {
        this.draft.set(value);
        // Ya se equivocó una vez: a partir de ahí sí se corrige mientras escribe.
        if (this.error()) this.error.set('');
    }

    submitPrompt(pending: DxcPending) {
        if (pending.mode !== 'prompt') return;
        const value = this.draft().trim();
        const message = pending.validate?.(value) ?? (value ? '' : 'Escribe un valor para continuar.');
        if (message) { this.error.set(message); return; }
        this.dialog.answerPrompt(value);
    }

    onScrim(event: MouseEvent) {
        if (this.leaving) return;
        if (event.target === event.currentTarget) this.dialog.dismiss();
    }

    @HostListener('document:keydown.escape')
    onEscape() { if (!this.leaving) this.dialog.dismiss(); }
}
